import { Fragment, ReactNode } from 'react'

import { getImageUrl } from '~/components/Image/helpers'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import supportsAlphaChannel from '~/utils/supportsAlphaChannel'
import { IconName } from '~/components/Icons'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import GraduationCapCircleIcon from '~/components/Icons/svgs/GraduationCapCircleIcon'
import CameraCircleIcon from '~/components/Icons/svgs/CameraCirclIcon'
import ChatCircleIcon from '~/components/Icons/svgs/ChatCircleIcon'
import CheckmarkIcon from '~/components/Icons/svgs/CheckmarkIcon'

import { getClassNames } from './ProductCard.utils'

const getIconComponent = (name: string): ((props: { [key: string]: any }) => React.ReactElement) => {
  switch (name) {
    case 'hom-graduation-cap-circle':
      return GraduationCapCircleIcon
    case 'hom-camera-circle':
      return CameraCircleIcon

    default:
      return ChatCircleIcon
  }
}

export default function ProductCard({
  button,
  colorScheme = 'purple',
  highlights,
  icon,
  price,
  title,
  video,
  videoCx,
  videoFallback,
  videoFallbackCx,
  videoFallbackHeight,
  videoFallbackWidth,
  shouldLoadVideos,
}: {
  button: ReactNode
  colorScheme?: 'sky' | 'white' | 'purple'
  highlights: string[]
  icon: IconName
  price: string
  title: string
  video: string
  videoCx?: string
  videoFallback: string
  videoFallbackCx?: string
  videoFallbackHeight: number
  videoFallbackWidth: number
  shouldLoadVideos: boolean
}): React.ReactElement {
  const { bg, checkmarkColor } = getClassNames(colorScheme)
  const mediaQuery = [{ screen: '(min-width: 280px)', width: `${videoFallbackWidth}px` }]
  return (
    <div className="relative isolate w-full">
      <span className="absolute z-20 top-0 left-1/2 -translate-y-1/2 -translate-x-1/2 drop-shadow-lg">
        <SVGIcon icon={getIconComponent(icon)} size={70} />
      </span>
      <div className="relative z-10 w-full max-w-sm h-[680px] overflow-hidden rounded shadow-large">
        <div className={`h-full flex flex-col ${bg}`}>
          <section className={`mt-4 p-8 pb-0 ${colorScheme === 'purple' ? 'text-white' : 'text-black-light'}`}>
            <div className="lg:h-56">
              <h3 className="text-center text-lg font-semibold pb-5">{title}</h3>
              <ul className="space-y-1.5">
                {highlights.map((elem) => (
                  <li className="space-x-4 flex justify-between" key={elem}>
                    <span>{elem}</span>
                    <span className="shrink-0">
                      <SVGIcon icon={CheckmarkIcon} pathClassName={checkmarkColor} size={20} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col items-center justify-between pt-5">
              <p className="text-lg lg:text-2xl font-semibold mb-4">{price}</p>
              <Fragment>{button}</Fragment>
            </div>
          </section>

          <div className="mt-auto">
            {supportsAlphaChannel() ? (
              <video
                autoPlay
                className={`mx-auto ${videoCx}`}
                loop
                muted
                playsInline
                poster={
                  getImageUrl(videoFallbackHeight, videoFallbackWidth, 'cover', videoFallback, 'hom-frontend-assets')
                    .urlWithFormat
                }
              >
                {shouldLoadVideos && (
                  <>
                    <source src={`${video}.mp4`} type="video/mp4;codecs=hvc1" />
                    <source src={`${video}.webm`} type="video/webm" />
                  </>
                )}
              </video>
            ) : (
              <ResponsiveImage
                alt={title}
                height={videoFallbackHeight}
                imageClassName={`mx-auto ${videoFallbackCx}`}
                imageWidths={`${videoFallbackWidth}`}
                mediaQuery={mediaQuery}
                srcKey={videoFallback}
                width={videoFallbackWidth}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
